import { ActivityEnums, AppEnum, AppEnumHelper, DateFormatHelper, OptionHelper, RecordMap } from "@/Shared";

import { UserModel, TaskModel } from '@/Domains';
import { ActivityModel } from '../Models';
import { ActivityFormValues } from "../ActivityFormValidation";

export interface ActivityFVMParams {
    viewEnums: ActivityEnums;
    taskMap: RecordMap<TaskModel>;
    userMap: RecordMap<UserModel>;
    activity?: ActivityModel;
    task? : TaskModel;
}

/**
 * =====================================================
 * ActivityFormViewModel
 * =====================================================
 *
 * ViewModel responsible for adapting Activity domain data
 * to the Activity form UI.
 *
 * Responsibilities:
 * - Transforms an ActivityModel into form-compatible values
 * - Resolves select options (agents, users, tasks)
 * - Exposes enums required by the form layer
 * - Centralizes form title and creation/edit mode logic
 *
 * This ViewModel acts as a boundary between:
 * - enriched domain models
 * - form state management (Formik)
 *
 * It contains no side effects and no UI logic.
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */
export class ActivityFormViewModel {

    /**
     * Cached select options derived from domain data
     */
    private _userOptions = [] as AppEnum[];
    private _taskOptions = [] as AppEnum[];

    //private _planning? : PlanningModel;
    //private _task? : TaskModel;

    /**
     * Human-readable task category label
     * used mainly for display purposes (title).
     */
    private _taskCategory!: string;

    public constructor(protected params: ActivityFVMParams) {
        const {
            viewEnums,
            taskMap,
            userMap,
            activity
        } = params;

        this._userOptions = OptionHelper.userOptions(userMap.toArray());
        this._taskOptions = OptionHelper.taskOptions(taskMap.toArray(), viewEnums.TaskCategoryEnum);
        this._taskCategory = AppEnumHelper.getLabel(viewEnums.TaskCategoryEnum, activity?.task?.category);
    }

    /**
     * Initial values consumed by the Activity form.
     * Values are normalized and formatted for form inputs.
     */
    get formValues() {
        return {
            taskId: this.params.task?.id ??  this.params.activity?.taskId,
            startDate: DateFormatHelper.toInput(this.params.activity?.startDate),
            dueDate: DateFormatHelper.toInput(this.params.activity?.startDate),
            assignedTo: String(this.params.activity?.assignedTo),
            initiatorId: String(this.params.activity?.initiatorId),
            priority: this.params.activity?.priority ?? "",
            status: this.params.activity?.status ?? "",
            completionRate: this.params.activity?.completionRate ?? 0,
            expenseNote: this.params.activity?.expenseNote ?? 0,
            description: this.params.activity?.description ?? "",
            offsetMs: DateFormatHelper.date().offsetMs,
            images: [],
            videos: [],
            videoURLs: []
        } as ActivityFormValues;
    }

    /**
     * Convenience accessors for form rendering
     */
    get id() { return this.params.activity?.id;}

    get statusOptions(): AppEnum[] { return this.params.viewEnums.ActivityStatusEnum; }
    get priorityOptions(): AppEnum[] { return this.params.viewEnums.ActivityPriorityEnum; }
    get userOptions(): AppEnum[] { return this._userOptions; }
    getTaskOptions(): AppEnum[] { return this._taskOptions; }

    /**
     * Form title adapts automatically
     * depending on creation or edition mode.
     */
    get title() {
        return this.params.activity
            ? "Activité N° " + this.id + " - " + this._taskCategory.toUpperCase()
            : "Création de activité";
    }

    /**
     * Indicates whether the form is in creation mode.
     */
    isCreationMode = () => !this.params.activity?.id;
}